import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { PhaseHeader } from "../components/PhaseHeader";
import { useCommand } from "../hooks/useCommand";
import { useScripts } from "../hooks/useScript";
import { joinUrl } from "../lib/links";
import { howToPlayPath } from "../lib/scripts";
import type {
  BadMoonRisingOdds,
  GameSettings,
  PlayerView,
  StorytellerOddsView,
} from "../protocol";
import { isHost, seatName } from "../state/selectors";
import { useGame } from "../state/store";

const MIN_PLAYERS = 5;
const MAX_PLAYERS = 15;

export function LobbyScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const { run, busy } = useCommand();
  const host = isHost(view);
  const n = view.seats.length;
  const canStart = n >= MIN_PLAYERS && n <= MAX_PLAYERS;

  const drag = useSeatDrag(view.seats, (ids) => run({ type: "reorder_seats", seat_ids: ids }));

  return (
    <main className="screen">
      <PhaseHeader view={view} roles={roles} showRole={false} />
      <ShareCard code={view.room_code} />
      <GamePicker config={view.config} host={host} />

      <section className="stack-sm">
        <div className="row-between">
          <h3>Seating</h3>
          <span className="small muted">
            {n} / {MAX_PLAYERS}
          </span>
        </div>
        <p className="small muted">
          Sit in this order, clockwise around the table. Voting and neighbours follow it.
          {host ? " Drag people by ⠿ to match where they sit." : ""}
        </p>
        <ol className="seat-list" ref={drag.listRef}>
          {drag.seats.map((s, i) => (
            <li
              key={s.seat_id}
              className={`card card-tight row-between${drag.dragging === s.seat_id ? " seat-dragging" : ""}`}
              style={drag.rowStyle(i)}
            >
              <div className="row" style={{ gap: 10, minWidth: 0 }}>
                <span className="faint mono small">{i + 1}</span>
                <span className={`dot${s.connected ? " dot-ok" : ""}`} aria-hidden />
                <span className="truncate">
                  {s.name}
                  {s.seat_id === view.me_seat_id && <span className="muted"> (you)</span>}
                </span>
                {s.is_host && <span className="chip tiny">host</span>}
              </div>
              {host && (
                <div className="row" style={{ gap: 0 }}>
                  <SeatMenu
                    name={s.name}
                    self={s.seat_id === view.me_seat_id}
                    onRename={(name) =>
                      run({ type: "rename_seat", target_seat_id: s.seat_id, name })
                    }
                    onKick={() => run({ type: "kick_seat", target_seat_id: s.seat_id })}
                    onMakeHost={() => run({ type: "transfer_host", target_seat_id: s.seat_id })}
                  />
                  <button
                    type="button"
                    className="icon-btn drag-handle"
                    aria-label={`Move ${s.name}`}
                    title="Drag to move. Arrow keys work too."
                    disabled={busy}
                    {...drag.handleProps(i)}
                  >
                    ⠿
                  </button>
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>

      {host && <Settings config={view.config} />}
      {host && view.storyteller_odds && <AdvancedSettings {...view.storyteller_odds} />}

      <div className="bottom-bar">
        {host ? (
          <>
            <button
              className="btn btn-primary"
              disabled={!canStart || busy}
              onClick={() => run({ type: "start_game" })}
            >
              Start the game
            </button>
            {!canStart && (
              <p className="small muted center">
                {n < MIN_PLAYERS
                  ? `Waiting for ${MIN_PLAYERS - n} more player${MIN_PLAYERS - n === 1 ? "" : "s"}.`
                  : `At most ${MAX_PLAYERS} players.`}
              </p>
            )}
          </>
        ) : (
          <p className="muted center">Waiting for {seatName(view, view.host_seat_id)} to start.</p>
        )}
      </div>
    </main>
  );
}

/** Which game the table plays. The host picks; everyone sees it and can read its rules. */
function GamePicker({ config, host }: { config: GameSettings; host: boolean }) {
  const scripts = useScripts();
  const { run, busy } = useCommand();
  const list = typeof scripts === "object" ? scripts : [];
  const current = list.find((s) => s.id === config.script_id);
  const rules = (
    // a new tab: leaving this page would drop the seat's connection
    <a className="small" href={howToPlayPath(config.script_id)} target="_blank" rel="noopener">
      How to play {current?.name ?? "this game"}
    </a>
  );
  if (!host || list.length < 2) {
    return (
      <section className="card card-tight stack-sm">
        <span className="eyebrow">Game</span>
        <strong>{current?.name ?? "…"}</strong>
        {current?.description && <span className="small muted">{current.description}</span>}
        {rules}
      </section>
    );
  }
  return (
    <section className="stack-sm">
      <h3>Game</h3>
      <div className="stack-sm" role="group" aria-label="Choose the game">
        {list.map((s) => (
          <button
            key={s.id}
            type="button"
            aria-pressed={s.id === config.script_id}
            className="btn btn-block game-option"
            disabled={busy}
            onClick={() =>
              s.id !== config.script_id &&
              run({ type: "update_config", config: { ...config, script_id: s.id } })
            }
          >
            <strong>{s.name}</strong>
            <span className="small">{s.description}</span>
          </button>
        ))}
      </div>
      {rules}
    </section>
  );
}

function ShareCard({ code }: { code: string }) {
  const publicUrl = useGame((s) => s.publicUrl);
  const url = joinUrl(code, publicUrl, window.location.origin);
  const [copied, setCopied] = useState(false);
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "TowerClock", text: `Join room ${code}`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* dismissed */
    }
  };
  return (
    <section className="card row" style={{ gap: 16, alignItems: "center" }}>
      <div className="qr">
        <QRCodeSVG value={url} size={112} bgColor="#efe9dc" fgColor="#0d0c1d" marginSize={2} />
      </div>
      <div className="stack-sm" style={{ minWidth: 0 }}>
        <span className="eyebrow">Room code</span>
        <span className="mono" style={{ fontSize: "1.8rem", fontWeight: 700 }}>
          {code}
        </span>
        <span className="tiny muted truncate" data-testid="join-link">
          {url.replace(/^https?:\/\//, "")}
        </span>
        <button className="btn btn-small" onClick={share}>
          {copied ? "Link copied" : "Share link"}
        </button>
      </div>
    </section>
  );
}

function SeatMenu({
  name,
  self,
  onRename,
  onKick,
  onMakeHost,
}: {
  name: string;
  self: boolean;
  onRename: (name: string) => void;
  onKick: () => void;
  onMakeHost: () => void;
}) {
  return (
    <select
      className="icon-btn seat-menu"
      aria-label={`Options for ${name}`}
      value=""
      onChange={(e) => {
        const choice = e.target.value;
        if (choice === "rename") {
          const next = window.prompt(`New name for ${name}`, name)?.trim();
          if (next && next !== name) onRename(next);
        }
        if (choice === "kick" && window.confirm(`Remove ${name} from the room?`)) onKick();
        if (choice === "host" && window.confirm(`Make ${name} the host?`)) onMakeHost();
      }}
    >
      <option value="">⋯</option>
      <option value="rename">Rename</option>
      {!self && <option value="host">Make host</option>}
      {!self && <option value="kick">Remove</option>}
    </select>
  );
}

type Rect = { top: number; height: number };
// rects and startY are in page coordinates, so the page may scroll during a drag
type Drag = { id: string; from: number; to: number; dy: number; startY: number; rects: Rect[] };

/** The host's seating list, reordered by dragging a row's handle (or its arrow keys). Rows show
 * the new order at once and stay there until the server's view replaces it. */
function useSeatDrag<S extends { seat_id: string }>(
  seats: S[],
  reorder: (ids: string[]) => Promise<unknown>,
) {
  const listRef = useRef<HTMLOListElement>(null);
  const [drag, setDragState] = useState<Drag | null>(null);
  // pointer events can outrun a render: handlers read the latest drag from here
  const dragRef = useRef<Drag | null>(null);
  const setDrag = (next: Drag | null) => {
    dragRef.current = next;
    setDragState(next);
  };
  const [pending, setPending] = useState<string[] | null>(null);
  const fingerY = useRef(0); // in the viewport

  const follow = () => {
    const d = dragRef.current;
    if (!d) return;
    const dy = fingerY.current + window.scrollY - d.startY;
    setDrag({ ...d, dy, to: target(d, dy) });
  };

  // a finger held near the top or bottom of the screen scrolls the page, to reach far seats
  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    let frame = 0;
    const step = () => {
      const edge = 80;
      const y = fingerY.current;
      const by = y < edge ? -8 : y > window.innerHeight - edge ? 8 : 0;
      if (by) {
        const before = window.scrollY;
        window.scrollBy(0, by);
        if (window.scrollY !== before) follow();
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow reads only refs
  }, [dragging]);

  const shown = pending ? pending.flatMap((id) => seats.filter((s) => s.seat_id === id)) : seats;
  const ordered = shown.length === seats.length ? shown : seats;

  const commit = (from: number, to: number) => {
    if (from === to || to < 0 || to >= ordered.length) return;
    const ids = ordered.map((s) => s.seat_id);
    const [id] = ids.splice(from, 1);
    ids.splice(to, 0, id);
    setPending(ids);
    void reorder(ids).finally(() => setPending(null));
  };

  const target = (d: Drag, dy: number) => {
    const me = d.rects[d.from];
    const centre = me.top + me.height / 2 + dy;
    let to = d.from;
    d.rects.forEach((r, i) => {
      const mid = r.top + r.height / 2;
      if (i > d.from && centre > mid) to = i;
      if (i < d.from && centre < mid && i < to) to = i;
    });
    return to;
  };

  const handleProps = (index: number) => ({
    onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
      const rows = listRef.current?.children;
      if (!rows || (e.pointerType === "mouse" && e.button !== 0)) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      const rects = Array.from(rows, (row) => {
        const r = row.getBoundingClientRect();
        return { top: r.top + window.scrollY, height: r.height };
      });
      const id = ordered[index].seat_id;
      fingerY.current = e.clientY;
      const startY = e.clientY + window.scrollY;
      setDrag({ id, from: index, to: index, dy: 0, startY, rects });
    },
    onPointerMove: (e: React.PointerEvent<HTMLButtonElement>) => {
      fingerY.current = e.clientY;
      follow();
    },
    onPointerUp: () => {
      const d = dragRef.current;
      if (!d) return;
      setDrag(null);
      commit(d.from, d.to);
    },
    onPointerCancel: () => setDrag(null),
    onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const step = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
      if (!step) return;
      e.preventDefault();
      commit(index, index + step);
    },
  });

  /** The dragged row follows the finger; the rows it passes make room for it. */
  const rowStyle = (i: number): React.CSSProperties | undefined => {
    if (!drag) return undefined;
    if (i === drag.from) return { transform: `translateY(${drag.dy}px)`, zIndex: 1 };
    const { from, to, rects } = drag;
    const pitch =
      from + 1 < rects.length
        ? rects[from + 1].top - rects[from].top
        : rects[from].top - rects[from - 1].top;
    let shift = 0;
    if (from < to && i > from && i <= to) shift = -pitch;
    if (to < from && i >= to && i < from) shift = pitch;
    return { transform: `translateY(${shift}px)`, transition: "transform 150ms ease" };
  };

  return { listRef, seats: ordered, dragging: drag?.id ?? null, handleProps, rowStyle };
}

type NumberKey = {
  [K in keyof GameSettings]: GameSettings[K] extends number ? K : never;
}[keyof GameSettings];

type TimerOption = { key: NumberKey; label: string; hint: string; options: [number, string][] };

const TIMER_OPTIONS: TimerOption[] = [
  {
    key: "min_discussion_seconds",
    label: "Talk before nominations",
    hint: "Nominations open after this much discussion each day.",
    options: [
      [0, "None"],
      [60, "1 min"],
      [120, "2 min"],
      [180, "3 min"],
      [300, "5 min"],
    ],
  },
  {
    key: "day_max_seconds",
    label: "Longest day",
    hint: "The day ends by itself after this long.",
    options: [
      [600, "10 min"],
      [900, "15 min"],
      [1200, "20 min"],
      [1800, "30 min"],
      [0, "No limit"],
    ],
  },
  {
    key: "speech_seconds",
    label: "Nomination speeches",
    hint: "Time for the nominator and nominee to speak before the vote.",
    options: [
      [0, "Skip"],
      [30, "30 s"],
      [60, "1 min"],
      [90, "90 s"],
      [120, "2 min"],
    ],
  },
  {
    key: "vote_seconds",
    label: "Time per vote",
    hint: "Each player's turn to raise a hand, clockwise.",
    options: [
      [5, "5 s"],
      [7, "7 s"],
      [10, "10 s"],
      [15, "15 s"],
    ],
  },
  {
    key: "night_prompt_seconds",
    label: "Night choices",
    hint: "How long a woken player has to choose. Then a random choice is made.",
    options: [
      [30, "30 s"],
      [45, "45 s"],
      [60, "1 min"],
      [90, "90 s"],
    ],
  },
];

// Offered only while every phone wakes at night: then being awake says nothing about a role.
// Information stays up 30 s, so a longer wait would never name a player reading theirs.
const STALL_OPTION: TimerOption = {
  key: "night_stall_seconds",
  label: "Name a slow player to the host",
  hint:
    "If a night turn sits untouched this long, the host's phone names that player so the host " +
    "can tell them to look. Shorter catches a stall sooner, but also names players who are " +
    "just thinking.",
  options: [
    [0, "Never"],
    [5, "5 s"],
    [10, "10 s"],
    [15, "15 s"],
    [20, "20 s"],
  ],
};

/** A labelled native <select> of numbers: a real control on every phone, iPhones included. */
function SelectField({
  id,
  label,
  hint,
  options,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  options: [number, string][];
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="input"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {options.map(([v, text]) => (
          <option key={v} value={v}>
            {text}
          </option>
        ))}
      </select>
      <span className="tiny faint">{hint}</span>
    </div>
  );
}

function TimerField({
  opt,
  value,
  disabled,
  onChange,
}: {
  opt: TimerOption;
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <SelectField
      id={opt.key}
      label={opt.label}
      hint={opt.hint}
      options={withCurrent(opt.options, value)}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

function Settings({ config }: { config: GameSettings }) {
  const [open, setOpen] = useState(false);
  const { run, busy } = useCommand();
  const update = (patch: Partial<GameSettings>) =>
    run({ type: "update_config", config: { ...config, ...patch } });

  return (
    <section className="stack-sm">
      <button
        className="btn btn-ghost btn-small"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? "Hide game settings" : "Game settings"}
      </button>
      {open && (
        <div className="card stack">
          {TIMER_OPTIONS.map((opt) => (
            <TimerField
              key={opt.key}
              opt={opt}
              value={config[opt.key]}
              disabled={busy}
              onChange={(value) => update({ [opt.key]: value })}
            />
          ))}
          <label className="row-between">
            <span>
              Tap every phone at night
              <br />
              <span className="tiny faint">
                Players with nothing to do pick a suspect instead, so nobody can tell who really
                woke. Each phone buzzes as it wakes, unless its player turns Vibrate off.
              </span>
            </span>
            <input
              type="checkbox"
              checked={config.night_decoys}
              disabled={busy}
              onChange={(e) => update({ night_decoys: e.target.checked })}
            />
          </label>
          {config.night_decoys && (
            <TimerField
              opt={STALL_OPTION}
              value={config.night_stall_seconds}
              disabled={busy}
              onChange={(value) => update({ night_stall_seconds: value })}
            />
          )}
        </div>
      )}
    </section>
  );
}

type OddsOption = { key: keyof BadMoonRisingOdds; label: string; hint: string };

// Bad Moon Rising's calls that the rules leave to the Storyteller.
const ODDS_OPTIONS: OddsOption[] = [
  {
    key: "sailor_townsfolk_drunk_p",
    label: "Sailor's choice is drunk: a Townsfolk",
    hint:
      "Each night the Sailor chooses a player, and either the Sailor or that player is drunk. " +
      "This is how often it is the player, when they are a Townsfolk.",
  },
  {
    key: "sailor_other_drunk_p",
    label: "Sailor's choice is drunk: anyone else",
    hint:
      "The same, when the Sailor chooses an Outsider, a Minion or the Demon. Higher means the " +
      "Sailor more often stops the Demon for a night.",
  },
  {
    key: "pacifist_save_p",
    label: "Pacifist saves an executed player",
    hint: "While the Pacifist is alive, how often a good player who is executed does not die.",
  },
  {
    key: "tinker_night_death_p",
    label: "Tinker dies at night",
    hint:
      "How often the Tinker dies on each night after the first. Never when it would end " +
      "the game.",
  },
  {
    key: "tinker_day_death_p",
    label: "Tinker dies by day",
    hint:
      "How often the Tinker dies at some moment during each day. Never during a vote, and never " +
      "when it would end the game.",
  },
  {
    key: "shabaloth_regurgitate_p",
    label: "Shabaloth brings someone back",
    hint:
      "How often, each night, a player the Shabaloth killed the night before comes back " +
      "to life.",
  },
  {
    key: "lunatic_other_demon_p",
    label: "Lunatic thinks they are another Demon",
    hint:
      "The Lunatic is told they are a Demon. This is how often it is a different Demon from the " +
      "one in the game.",
  },
];

const PERCENTS = Array.from({ length: 21 }, (_, i) => i * 5);

/** 0% to 100% in steps of 5, plus the current value if it falls between them. */
function percentOptions(current: number, usual: number): [number, string][] {
  const values = [...new Set([...PERCENTS, current, usual])].sort((a, b) => a - b);
  return values.map((v) => [v, v === usual ? `${v}% (default)` : `${v}%`]);
}

const percent = (p: number) => Math.round(p * 100);

/** The host's lobby, with Bad Moon Rising chosen: how often the Storyteller makes each call. */
function AdvancedSettings({ odds, defaults }: StorytellerOddsView) {
  const [open, setOpen] = useState(false);
  const { run, busy } = useCommand();
  const update = (next: BadMoonRisingOdds) => run({ type: "update_storyteller_odds", odds: next });
  const usual = ODDS_OPTIONS.every((o) => odds[o.key] === defaults[o.key]);

  return (
    <section className="stack-sm">
      <button
        className="btn btn-ghost btn-small"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? "Hide advanced settings" : "Advanced settings"}
      </button>
      {open && (
        <div className="card stack">
          <p className="small muted">
            Bad Moon Rising leaves some calls to the Storyteller. These set how often the app makes
            each one. Only your phone shows them.
          </p>
          {ODDS_OPTIONS.map((opt) => (
            <SelectField
              key={opt.key}
              id={`odds-${opt.key}`}
              label={opt.label}
              hint={opt.hint}
              options={percentOptions(percent(odds[opt.key]), percent(defaults[opt.key]))}
              value={percent(odds[opt.key])}
              disabled={busy}
              onChange={(value) => update({ ...odds, [opt.key]: value / 100 })}
            />
          ))}
          <button
            className="btn btn-small"
            disabled={busy || usual}
            onClick={() => update(defaults)}
          >
            Reset to defaults
          </button>
        </div>
      )}
    </section>
  );
}

function withCurrent(options: [number, string][], current: number): [number, string][] {
  return options.some(([v]) => v === current) ? options : [...options, [current, `${current} s`]];
}
